"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUpRight, Cloud, X } from "lucide-react";
import LogoXS from "@/components/shared/LogoXS";
import "./release-update-popup.css";

const SEEN_KEY = "fillapp:update-2.2:seen";
const UPDATE_URL = "/updates/2.2/index.html?preview=cloud";

export default function ReleaseUpdatePopup() {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const [bodyHeight, setBodyHeight] = useState(800);
  const [bodyReady, setBodyReady] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bodyRef = useRef<HTMLIFrameElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const show = useCallback(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setExpanded(false);
    setReady(false);
    setBodyReady(false);
    setBodyHeight(800);
    setOpen(true);
    setAvailable(true);
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* Storage is optional. */
    }
  }, []);
  const close = useCallback(() => {
    dialogRef.current?.close();
    setOpen(false);
    setExpanded(false);
    returnFocusRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    setMounted(true);
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {
      /* Storage is optional. */
    }
    if (seen) {
      setAvailable(true);
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const reveal = () => {
      if (
        document.hidden ||
        document.querySelector(
          'dialog[open], [role="dialog"][data-state="open"]',
        )
      ) {
        timer = setTimeout(reveal, 1000);
        return;
      }
      show();
    };
    timer = setTimeout(reveal, 4000);
    return () => clearTimeout(timer);
  }, [show]);
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    dialogRef.current.showModal();
    popupRef.current?.focus({ preventScroll: true });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const message = (event: MessageEvent) => {
      if (event.origin !== location.origin) return;
      const cloud = event.source === iframeRef.current?.contentWindow;
      const body = event.source === bodyRef.current?.contentWindow;
      if (!cloud && !body) return;
      if (event.data?.type === "fillapp-update-ready" && cloud) setReady(true);
      if (event.data?.type === "fillapp-update-close") close();
      if (
        body &&
        event.data?.type === "fillapp-update-size" &&
        Number.isFinite(event.data.height) &&
        event.data.height > 100 &&
        event.data.height < 20000
      ) {
        setBodyHeight(Math.ceil(event.data.height));
        setBodyReady(true);
      }
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("message", message);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("message", message);
      window.removeEventListener("keydown", key);
    };
  }, [open, close]);

  if (!mounted) return null;
  return createPortal(
    <>
      {available && !open && (
        <button className="release-reopen" onClick={show}>
          <span />
          What’s new <strong>2.2</strong>
          <ArrowUpRight size={15} />
        </button>
      )}
      {open && (
        <dialog
          ref={dialogRef}
          className="release-dialog"
          aria-labelledby="release-popup-title"
          data-expanded={expanded}
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            ref={popupRef}
            tabIndex={-1}
            className="release-popup"
            data-expanded={expanded}
          >
            <div className="release-content-scroll">
              <section
                className="release-hero"
                aria-labelledby="release-popup-title"
              >
                <div className="release-popup-brand">
                  <span>What’s new</span>
                  <strong>2.2</strong>
                </div>
                <div className="release-popup-copy">
                  <span className="release-popup-eyebrow">
                    <Cloud size={16} />
                    Continue in cloud
                  </span>
                  <h2 id="release-popup-title">
                    Close your laptop.
                    <br />
                    Keep the task going.
                  </h2>
                  <p>
                    Move your task and logins to the cloud. FillApp keeps
                    working, even after you close your laptop.
                  </p>
                  <span className="release-popup-more">
                    Plus voice mode, shortcuts, and more.
                  </span>
                </div>
                <div className="release-update-frame" data-ready={ready}>
                  {!ready && (
                    <div className="release-loading">
                      <LogoXS />
                      <span>Loading the update…</span>
                    </div>
                  )}
                  <iframe
                    ref={iframeRef}
                    src={UPDATE_URL}
                    title="Continue in cloud animation"
                    onLoad={() => setReady(true)}
                  />
                </div>
                <div className="release-popup-actions" aria-hidden={expanded}>
                  <button
                    className="release-keep-reading"
                    onClick={() => setExpanded(true)}
                    disabled={!ready}
                    tabIndex={expanded ? -1 : 0}
                  >
                    Keep reading
                    <ArrowDown size={18} />
                  </button>
                </div>
              </section>
              {expanded && (
                <iframe
                  ref={bodyRef}
                  className="release-body-frame"
                  data-ready={bodyReady}
                  src="/updates/2.2/index.html?content=after-cloud"
                  title="More in the FillApp 2.2 update"
                  style={{ height: bodyHeight }}
                />
              )}
            </div>
            <button
              className="release-close"
              aria-label="Close update"
              onClick={close}
            >
              <X size={21} />
            </button>
          </div>
        </dialog>
      )}
    </>,
    document.body,
  );
}
